/**
 * Fuso horário do restaurante (IANA) e "agora no restaurante".
 *
 * Código puro (sem DOM, sem bindings): roda igual no Worker, no admin e no
 * futuro `client-core`. A base de fusos é o tzdata do próprio runtime
 * (ICU do V8/workerd/browser) via `Intl` — nada de lista fixa no código que
 * envelhece a cada mudança de regra de horário de verão.
 *
 * Base para as janelas de promoção e de prato do dia ("naquele dia ou naquele
 * horário") — sempre no fuso do restaurante, nunca no do servidor/cliente.
 */

/** Fuso padrão da plataforma quando o país não tem mapeamento. */
export const DEFAULT_TIMEZONE = 'Europe/Lisbon'

/**
 * Fuso padrão por país (ISO 3166-1 alpha-2), usado no backfill da migração
 * 0044 e na criação de tenants. Países com mais de um fuso recebem o da
 * capital/maior mercado — o dono ajusta no admin (ex.: Açores, Manaus).
 * Manter em sincronia com o CASE da migração 0044.
 */
export const COUNTRY_DEFAULT_TIMEZONE: Readonly<Record<string, string>> = {
  PT: 'Europe/Lisbon',
  BR: 'America/Sao_Paulo',
  ES: 'Europe/Madrid',
  US: 'America/New_York',
}

/** Fuso padrão para o país do tenant (fallback: {@link DEFAULT_TIMEZONE}). */
export function defaultTimezoneForCountry(countryCode: string | null | undefined): string {
  return COUNTRY_DEFAULT_TIMEZONE[(countryCode ?? '').toUpperCase()] ?? DEFAULT_TIMEZONE
}

// `Area/Local[/Sub]` (ex.: America/Argentina/Buenos_Aires) ou `UTC`.
// Recusa abreviações ambíguas (EST, CET, WET) e offsets fixos (GMT+1,
// Etc/GMT+3), que não seguem horário de verão.
const AREA = /^[A-Z][A-Za-z_]*$/
const LOCATION = /^[A-Za-z0-9_+-]+$/

function hasIanaShape(tz: string): boolean {
  if (tz === 'UTC') return true
  const [area, ...rest] = tz.split('/')
  if (area === undefined || area === 'Etc' || !AREA.test(area)) return false
  return rest.length >= 1 && rest.length <= 2 && rest.every((p) => LOCATION.test(p))
}

let supportedZones: ReadonlySet<string> | null | undefined

/** Fusos do runtime (`Intl.supportedValuesOf`), ou `null` se indisponível. */
function runtimeZones(): ReadonlySet<string> | null {
  if (supportedZones === undefined) {
    try {
      supportedZones = new Set(Intl.supportedValuesOf('timeZone'))
    } catch {
      supportedZones = null
    }
  }
  return supportedZones
}

/**
 * Nome canônico do fuso segundo o runtime (`Intl`), ou `null` se o runtime
 * não conhece o fuso. Ex.: `America/sao_paulo` → `America/Sao_Paulo`,
 * `US/Eastern` → `America/New_York`.
 */
export function canonicalTimezone(tz: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz }).resolvedOptions().timeZone
  } catch {
    return null
  }
}

/**
 * `true` se `tz` é um nome IANA aceito pelo runtime (`Intl`) no formato
 * `Area/Local` (ou `UTC`) E escrito na forma canônica — o banco guarda
 * exatamente o nome que o runtime usa. Recusa grafia com caixa diferente
 * (`America/sao_paulo`) e aliases legados (`US/Eastern`, `GB`): o nome tem de
 * ser igual ao canônico de `Intl` ou constar em `Intl.supportedValuesOf`.
 * `UTC` é sempre aceito.
 */
export function isValidTimezone(tz: string): boolean {
  if (typeof tz !== 'string' || tz.length > 64 || !hasIanaShape(tz)) return false
  const canonical = canonicalTimezone(tz)
  if (canonical === null) return false
  if (tz === 'UTC' || tz === canonical) return true
  return runtimeZones()?.has(tz) ?? false
}

/**
 * Resolve um fuso JÁ GRAVADO no banco para uso em cálculo, sem lançar.
 * Dados antigos (antes do #239) podem ter grafia não canônica
 * (`America/sao_paulo`): tenta o canônico do runtime e, se preciso, uma
 * busca sem diferenciar caixa em `Intl.supportedValuesOf`. Devolve `null`
 * se não houver fuso válido — quem chama decide o fallback (ex.: janelas de
 * promoção ficam inativas), nunca um 500.
 */
export function resolveStoredTimezone(tz: string | null | undefined): string | null {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64) return null
  if (isValidTimezone(tz)) return tz
  const canonical = canonicalTimezone(tz)
  if (canonical !== null && tz.includes('/') && isValidTimezone(canonical)) return canonical
  const lower = tz.toLowerCase()
  for (const z of runtimeZones() ?? []) {
    if (z.toLowerCase() === lower && isValidTimezone(z)) return z
  }
  return null
}

/** Mensagem de erro para um fuso recusado (sugere o nome canônico quando há). */
export function invalidTimezoneMessage(tz: unknown): string {
  const canonical = typeof tz === 'string' && tz.length <= 64 ? canonicalTimezone(tz) : null
  // Só sugere para nomes no formato Area/Local (ex.: caixa errada, US/Eastern);
  // abreviações como EST resolvem para fusos arbitrários (America/Panama).
  if (
    typeof tz === 'string' &&
    tz.includes('/') &&
    canonical &&
    canonical !== tz &&
    isValidTimezone(canonical)
  ) {
    return `Fuso horário "${tz}" não está na forma canônica IANA; use "${canonical}".`
  }
  return 'Fuso horário deve ser um nome IANA canônico, ex.: Europe/Lisbon'
}

/** Instante decomposto no relógio de parede do restaurante. */
export type RestaurantNow = {
  /** Fuso usado no cálculo. */
  timezone: string
  /** Data local `YYYY-MM-DD`. */
  date: string
  /** 0=Domingo … 6=Sábado (mesma convenção de `operating_hours`). */
  dayOfWeek: number
  /** Hora local `HH:MM` (00:00–23:59), comparável com `start_time`/`end_time`. */
  time: string
  /** Minutos desde a meia-noite local (0–1439). */
  minutes: number
}

const WEEKDAYS: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function formatterFor(tz: string): Intl.DateTimeFormat {
  let fmt = formatterCache.get(tz)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    formatterCache.set(tz, fmt)
  }
  return fmt
}

/**
 * "Agora no restaurante": dia da semana, data e `HH:MM` no fuso `tz`.
 *
 * `now` é injetável (testes, cálculo para um instante passado/futuro).
 * Fuso inválido lança `RangeError` — valide antes com {@link isValidTimezone}
 * (o banco só guarda fusos validados).
 */
export function nowInRestaurant(tz: string, now: Date | number = Date.now()): RestaurantNow {
  if (!isValidTimezone(tz)) throw new RangeError(`Fuso horário inválido: ${tz}`)
  const parts: Record<string, string> = {}
  for (const p of formatterFor(tz).formatToParts(now)) parts[p.type] = p.value
  // alguns motores antigos devolvem "24" à meia-noite mesmo com h23
  const hour = parts.hour === '24' ? '00' : (parts.hour ?? '00')
  const minute = parts.minute ?? '00'
  return {
    timezone: tz,
    date: `${parts.year}-${parts.month}-${parts.day}`,
    dayOfWeek: WEEKDAYS[parts.weekday ?? ''] ?? 0,
    time: `${hour}:${minute}`,
    minutes: Number(hour) * 60 + Number(minute),
  }
}

/**
 * Formata um instante no RELÓGIO DO RESTAURANTE (#248), não no fuso do
 * dispositivo. `instant` em milissegundos (ou Date). Sem fuso conhecido (ou
 * inválido), cai no fuso do dispositivo — melhor que quebrar a tela.
 */
export function formatInRestaurant(
  instant: Date | number,
  locale: string | undefined,
  tz: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
  const opts: Intl.DateTimeFormatOptions = { ...options }
  if (tz && isValidTimezone(tz)) opts.timeZone = tz
  try {
    return new Intl.DateTimeFormat(locale, opts).format(instant)
  } catch {
    // locale inválido vindo de config/cookie: formata no padrão do runtime
    return new Intl.DateTimeFormat(undefined, opts).format(instant)
  }
}
